import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Send, Sparkles, FileText, Loader2, Eye, Code,
  Mail, RefreshCw, Wand2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailComposer = ({ 
  isOpen, 
  onClose, 
  contact = null,
  onEmailSent = () => {}
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [viewMode, setViewMode] = useState('edit'); // edit, preview
  
  const [email, setEmail] = useState({
    to_email: contact?.email || '',
    subject: '',
    body_html: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      if (contact?.email) {
        setEmail(prev => ({ ...prev, to_email: contact.email }));
      }
    }
  }, [isOpen, contact]);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email/templates?token=${token}`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates', error);
    }
  };

  const applyTemplate = (template) => {
    setSelectedTemplate(template);
    
    // Apply template variables
    let subject = template.subject;
    let body = template.body_html;
    
    const variables = {
      name: contact?.name || contact?.first_name || '',
      first_name: contact?.first_name || contact?.name?.split(' ')[0] || '',
      last_name: contact?.last_name || '',
      company: contact?.company || '',
      email: contact?.email || '',
      phone: contact?.phone || '',
      sender_name: 'Sales Team' // Could be user's name
    };
    
    Object.entries(variables).forEach(([key, value]) => {
      subject = subject.replace(new RegExp(`{{${key}}}`, 'g'), value);
      body = body.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    
    setEmail({ ...email, subject, body_html: body });
    toast.success(`Template "${template.name}" applied`);
  };

  const generateWithAI = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/email/generate?token=${token}`, {
        type: 'follow_up',
        contact_name: contact?.name || contact?.first_name || '',
        company: contact?.company || '',
        context: email.subject || 'Follow up on our previous conversation',
        tone: 'professional'
      });
      
      if (response.data.success) {
        setEmail({
          ...email,
          subject: response.data.subject,
          body_html: response.data.body_html
        });
        toast.success('Email generated with AI');
      }
    } catch (error) {
      console.error('AI generation failed', error);
      toast.error('Failed to generate email');
    } finally {
      setGenerating(false);
    }
  };

  const sendEmail = async () => {
    if (!email.to_email || !email.subject || !email.body_html) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setLoading(true);
    try {
      // Use the Microsoft 365 email API
      const response = await axios.post(`${API}/email/send-to-contact/${contact?.id}?token=${token}`, {
        subject: email.subject,
        body: email.body_html
      });
      
      if (response.data.success) {
        // Check HubSpot sync status
        if (response.data.hubspot_synced) {
          toast.success('Email sent and synced to HubSpot!');
        } else if (response.data.hubspot_sync_warning) {
          toast.success('Email sent successfully');
          toast.warning(`HubSpot sync failed: ${response.data.hubspot_sync_warning}`, { duration: 5000 });
        } else {
          toast.success('Email sent successfully!');
        }
        onEmailSent(response.data);
        onClose();
        setEmail({ to_email: '', subject: '', body_html: '' });
        setSelectedTemplate(null);
      } else {
        toast.error(response.data.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Send email failed', error);
      const errorMsg = error.response?.data?.detail || 'Failed to send email. Please check email settings.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const renderPreview = () => {
    return (
      <div className="border rounded-lg p-6 bg-white min-h-[300px]">
        <div className="border-b pb-4 mb-4">
          <p className="text-sm text-slate-500">To: <span className="text-slate-700">{email.to_email}</span></p>
          <p className="text-sm text-slate-500">Subject: <span className="text-slate-700 font-medium">{email.subject}</span></p>
        </div>
        <div 
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: email.body_html }}
        />
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="email-composer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail size={20} />
            Compose Email
            {contact && (
              <Badge variant="outline" className="ml-2">
                To: {contact.name || contact.email}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
          {/* Templates Sidebar */}
          <div className="lg:col-span-1 border-r pr-4">
            <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
              <FileText size={14} />
              Email Templates
            </h4>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {templates.map(template => (
                <div
                  key={template.id}
                  onClick={() => applyTemplate(template)}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors hover:bg-slate-50 ${
                    selectedTemplate?.id === template.id ? 'border-primary bg-primary/5' : ''
                  }`}
                  data-testid={`template-${template.id}`}
                >
                  <p className="font-medium text-sm">{template.name}</p>
                  <p className="text-xs text-slate-500 mt-1 truncate">{template.subject}</p>
                  <Badge variant="outline" className="text-xs mt-2 capitalize">
                    {template.category}
                  </Badge>
                </div>
              ))}
            </div>
            
            <Button 
              variant="outline" 
              className="w-full mt-4 gap-2"
              onClick={generateWithAI}
              disabled={generating}
              data-testid="generate-ai-btn"
            >
              {generating ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Wand2 size={14} />
              )}
              Generate with AI
            </Button>
          </div>

          {/* Email Composer */}
          <div className="lg:col-span-2">
            <Tabs value={viewMode} onValueChange={setViewMode}>
              <TabsList className="mb-4">
                <TabsTrigger value="edit" className="gap-2">
                  <Code size={14} />
                  Edit
                </TabsTrigger>
                <TabsTrigger value="preview" className="gap-2">
                  <Eye size={14} />
                  Preview
                </TabsTrigger>
              </TabsList>

              <TabsContent value="edit" className="space-y-4">
                <div>
                  <Label htmlFor="email-to">To</Label>
                  <Input
                    id="email-to"
                    type="email"
                    value={email.to_email}
                    onChange={(e) => setEmail({ ...email, to_email: e.target.value })}
                    placeholder="recipient@example.com"
                    data-testid="email-to-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email-subject">Subject</Label>
                  <Input
                    id="email-subject"
                    value={email.subject}
                    onChange={(e) => setEmail({ ...email, subject: e.target.value })}
                    placeholder="Email subject..."
                    data-testid="email-subject-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email-body">Message (HTML)</Label>
                  <Textarea
                    id="email-body"
                    value={email.body_html}
                    onChange={(e) => setEmail({ ...email, body_html: e.target.value })}
                    placeholder="<div>Your email content here...</div>"
                    className="min-h-[250px] font-mono text-sm"
                    data-testid="email-body-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Use HTML formatting. Variables: {'{{name}}'}, {'{{company}}'}, {'{{email}}'}
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="preview">
                {renderPreview()}
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <Button variant="outline" onClick={onClose} data-testid="cancel-email-btn">
                Cancel
              </Button>
              <Button 
                onClick={sendEmail}
                disabled={loading || !email.to_email || !email.subject}
                className="gap-2"
                data-testid="send-email-btn"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                Send Email
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmailComposer;
